import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { setSeoPageCache } from "@/lib/page-cache";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Reveal } from "@/components/Reveal";
import { HELP_ARTICLES, getHelpArticle } from "@/data/help-articles";

export const Route = createFileRoute("/help/$slug")({
 loader: async ({ params }) => {
 await setSeoPageCache();
 const article = getHelpArticle(params.slug);
 if (!article) throw notFound();
 return { article };
 },
 head: ({ loaderData, params }) => {
 if (!loaderData) return { meta: [{ title: "Article not found, Heallexa" }] };
 const { article } = loaderData;
 const url = `https://heallexa.com/help/${params.slug}`;
 return {
 meta: [
 { title: `${article.title}, Heallexa` },
 { name: "description", content: article.description },
 { property: "og:title", content: article.title },
 { property: "og:description", content: article.description },
 { property: "og:url", content: url },
 { property: "og:type", content: "article" },
 ],
 links: [{ rel: "canonical", href: url }],
 scripts: [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "Article",
 headline: article.title,
 description: article.description,
 datePublished: article.updated,
 dateModified: article.updated,
 author: { "@type": "Organization", name: "Heallexa" },
 publisher: { "@type": "Organization", name: "Heallexa" },
 mainEntityOfPage: url,
 }),
 },
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "FAQPage",
 mainEntity: article.faqs.map((f) => ({
 "@type": "Question",
 name: f.q,
 acceptedAnswer: { "@type": "Answer", text: f.a },
 })),
 }),
 },
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "Help Center", item: "https://heallexa.com/help" },
 { "@type": "ListItem", position: 2, name: article.title, item: url },
 ],
 }),
 },
 ],
 };
 },
 notFoundComponent: () => (
 <div className="min-h-screen bg-background">
 <Navbar />
 <div className="mx-auto max-w-3xl px-6 py-32 text-center">
 <h1 className="font-display text-3xl font-semibold">Article not found</h1>
 <p className="mt-3 text-muted-foreground">This help article doesn't exist.</p>
 <Link to="/help" className="mt-6 inline-block text-primary hover:underline">← Back to Help Center</Link>
 </div>
 </div>
 ),
 errorComponent: ({ error }) => (
 <div className="min-h-screen bg-background">
 <Navbar />
 <div className="mx-auto max-w-3xl px-6 py-32 text-center">
 <h1 className="font-display text-3xl font-semibold">Something went wrong</h1>
 <p className="mt-3 text-muted-foreground">{error.message}</p>
 </div>
 </div>
 ),
 component: HelpArticlePage,
});

function HelpArticlePage() {
 const { article } = Route.useLoaderData();
 const related = HELP_ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 3);

 return (
 <div className="min-h-screen bg-background pb-1">
 <Navbar />

 <header className="pt-32 md:pt-36 pb-12 border-b border-border bg-card">
 <div className="mx-auto max-w-[720px] px-6">
 <nav className="text-[13px] text-muted-foreground mb-4">
 <Link to="/help" className="hover:text-foreground">Help Center</Link>
 <span className="mx-2">/</span>
 <span className="text-foreground">{article.title}</span>
 </nav>
 <h1 className="text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground">
 {article.title}
 </h1>
 <p className="mt-3 text-[14px] text-muted-foreground max-w-[520px]">{article.description}</p>
 <p className="mt-4 text-[12px] text-muted-foreground">
 Updated {new Date(article.updated).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
 </p>
 </div>
 </header>

 <main className="mx-auto max-w-[720px] px-6 py-16">
 <article className="max-w-none">
 <p className="text-[15px] leading-7 text-foreground/80">{article.intro}</p>

 {article.sections.map((s: any) => (
 <section key={s.h} className="mt-10">
 <h2 className="text-[18px] font-semibold tracking-tight text-foreground">{s.h}</h2>
 <p className="mt-3 text-[15px] leading-7 text-foreground/80">{s.p}</p>
 </section>
 ))}

 <Reveal from="up" delay={60} as="section" className="mt-14">
 <h2 className="text-[18px] font-semibold tracking-tight text-foreground">Frequently asked questions</h2>
 <div className="card-surface rounded-md mt-4 divide-y divide-border">
 {article.faqs.map((f: any) => (
 <details key={f.q} className="group p-5">
 <summary className="cursor-pointer text-[14px] font-medium text-foreground list-none flex justify-between items-center">
 {f.q}
 <span className="text-muted-foreground group-open:rotate-45 transition-transform">+</span>
 </summary>
 <p className="mt-3 text-[13.5px] text-muted-foreground leading-relaxed">{f.a}</p>
 </details>
 ))}
 </div>
 </Reveal>

 <Reveal from="up" delay={60} as="section" className="mt-14">
 <div className="card-surface p-10 text-center rounded-md">
 <p className="text-[14px] font-medium text-foreground">Ready to find a doctor?</p>
 <p className="mt-1 text-[13.5px] text-muted-foreground">Search verified providers by specialty, location, and insurance.</p>
 <Link
 to="/browse"
 className="mt-6 inline-flex items-center justify-center gap-2 h-11 px-5 text-[14px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded-md"
 >
 Browse providers
 </Link>
 </div>
 </Reveal>

 {related.length > 0 && (
 <Reveal from="up" delay={60} as="section" className="mt-14">
 <h2 className="text-[18px] font-semibold tracking-tight text-foreground">Related guides</h2>
 <ul className="mt-4 space-y-3">
 {related.map((r) => (
 <li key={r.slug}>
 <Link to="/help/$slug" params={{ slug: r.slug }} className="text-[14px] text-primary hover:underline">
 {r.title}
 </Link>
 <p className="text-[13.5px] text-muted-foreground">{r.description}</p>
 </li>
 ))}
 </ul>
 </Reveal>
 )}
 </article>
 </main>

 <FooterSection />
 </div>
 );
}
